/**
 * Frontend types aligned to `$SYNC_ROOT/contract/web.md`.
 */

export type CallState =
  | "IDLE"
  | "ORIGINATING"
  | "CLASSIFYING"
  | "ACCEPT_GATE"
  | "COLLECTING"
  | "PARKED"
  | "PLAYING"
  | "HANGING_UP"
  | "DONE";

export type OperatorAction =
  | "Accept"
  | "Retry"
  | "Resend"
  | "Timeout"
  | "Hangup";

export type SipLight = "REGED" | "NOREG" | "FAIL_WAIT" | "UNKNOWN" | "offline";

export type RunMode = "live" | "offline" | "auto";

export type LeadStatus =
  | "pending"
  | "queued"
  | "in_progress"
  | "done"
  | "failed"
  | "skipped";

export interface UiActions {
  accept: boolean;
  retry: boolean;
  resend: boolean;
  hangup: boolean;
  otp_sent: boolean;
  new_call: boolean;
  redial: boolean;
  next_lead: boolean;
  listen?: boolean;
}

export interface CallParty {
  dest: string | null;
  name: string | null;
  cli: string | null;
  amd: string | null;
  attempt: number;
  max_attempts: number;
  park_remaining_s: number | null;
  last_decision: OperatorAction | null;
  hangup_cause: string | null;
  uuid: string | null;
}

export interface ConsoleSnapshot {
  state: CallState;
  detail: string | null;
  heroCopy: string | null;
  party: CallParty;
  digits: string;
  expected_code: string | null;
  codeMatch: boolean | null;
  actions: UiActions;
  listening?: boolean;
  sip: SipLight;
  sipText: string;
  sipCss: string;
  mode: RunMode;
  /** AMD/RHD decision: HUMAN / MACHINE / SCREENING / UNKNOWN. */
  amd_label?: string | null;
  /** Far-end class (VOICEMAIL / HUMAN / …). Distinct from AMD MACHINE. */
  far_end_class?: string | null;
  judgment_outcome?: string | null;
  trunk_id?: string | null;
  otp_state?: string | null;
  /** IVR graph slug or speech-pack slug when the hello/snapshot named one. */
  scriptId?: string | null;
  /** Current graph node id. Empty on the linear office pack. */
  scriptNode?: string | null;
  /** Spoken language from the live session, when the API sent it. */
  spokenLanguage?: string | null;
  /** `otp` paints the strip. `gate` must not. */
  digitKind?: "otp" | "gate" | "";
}

export interface LogLine {
  id: string;
  ts: string;
  line: string;
}

export interface Lead {
  id: number | null;
  phone: string;
  customer_name: string;
  expected_code: string | null;
  purpose: string | null;
  extra?: Record<string, unknown>;
  status: LeadStatus;
}

export interface OperatorDecisionRow {
  ts?: string | null;
  action?: string | null;
  digits?: string | null;
  attempt?: number | null;
}

export interface HistoryFilters {
  q?: string;
  amd?: string;
  match?: string;
  outcome?: string;
}

export interface CallRecord {
  id: string;
  lead_id?: number | null;
  timestamp: string;
  destination: string;
  cli_used?: string | null;
  customer_name?: string | null;
  amd_result?: string | null;
  call_type?: string | null;
  digits_attempts?: string[];
  operator_decisions?: OperatorDecisionRow[];
  /** Client-entered OTP/DTMF from `/api/history`. Raw (operator confirm policy); `len:N` only for legacy warehouse tokens. */
  code?: string | null;
  /** Alias of {@link CallRecord.code}. */
  digits?: string | null;
  /** Expected OTP, raw, so the operator can confirm expected-vs-entered (ephemeral). `null` when absent or a legacy token. */
  expected_code?: string | null;
  final_match?: boolean | null;
  duration_ms?: number | null;
  hangup_cause?: string | null;
  /** Basename only. Never a directory path. */
  recording_path?: string | null;
  trunk_id?: string | null;
  country_iso2?: string | null;
  far_end_class?: string | null;
  success_class?: string | null;
  judgment_outcome?: string | null;
  /**
   * Raw stored CDR judgment (expected-code match), verbatim from the
   * warehouse. Never the call Outcome — UI renders it as a neutral
   * `code-match: X` chip. Falls back to {@link CallRecord.judgment_outcome}
   * when the API predates the split.
   */
  judgment_raw?: string | null;
  /** Derived History Outcome enum. Not CPA judgment_outcome. */
  outcome?: string | null;
  otp_state?: string | null;
  media_quality_band?: string | null;
  /**
   * On-disk recording size in bytes (history API evidence field).
   * `null` = file missing or backend could not stat it. Compared against
   * {@link CallRecord.duration_ms} to flag truncated audio evidence.
   */
  recording_bytes?: number | null;
}

export interface SipProfile {
  name: string;
  sofia_gateway_id: string;
  proxy: string;
  transport: "udp" | "tcp" | "tls";
  username: string;
  realm: string;
  register: boolean;
  caller_id_in_from: boolean;
  from_user: string;
  ping: number | null;
  retry_seconds?: number;
  expire_seconds: number;
  codecs: string;
  allowed_countries?: string[];
  active?: boolean;
  sofia_state?: string | null;
  password_set?: boolean;
}

export interface SipProfileWrite extends Partial<SipProfile> {
  name: string;
  proxy: string;
  password?: string;
}

export interface DtmfRangeResult {
  ok?: boolean;
  sofia_gateway_id?: string;
  sofia?: { name?: string; state?: string; profile?: string };
  dtmf?: {
    rfc2833_pt?: number;
    window_ms?: number;
    hop_ms?: number;
    confirm_windows?: number;
    sip_info?: boolean;
  };
  synthetic?: { ok?: boolean; window_ms?: number; error?: string };
  live_digit_test?: string;
}

export interface CallSetup {
  cli: string;
  dest: string;
  customer_name: string;
  script?: string;
  /** Optional no-code IVR graph slug. Empty → locked office pack engine. */
  ivr_id?: string;
  otp_length: number;
  business_name?: string;
  caller_name?: string;
  expected_code?: string | null;
  purpose?: string | null;
  scripts?: Record<string, string>;
  identity_replay: boolean;
  avmd_enabled: boolean;
  recordings_enabled: boolean;
  profile_name?: string;
  spoken_language?: string;
  pack?: string;
  sip_gateway_id?: string;
  country?: string;
  allowed_countries?: string[];
  script_vars?: Record<string, string>;
}

export interface PackInfo {
  slug: string;
  label: string;
}

export type IvrNodeType =
  | "start"
  | "say"
  | "dtmf"
  | "hold"
  | "queue"
  | "sms"
  | "transfer"
  | "gate"
  | "capture"
  | "park"
  | "hangup"
  | "identity"
  | "pickup"
  | "wait_operator"
  | "wait";

export interface IvrNode {
  id: string;
  type: IvrNodeType | string;
  label?: string;
  prompt?: string;
  file_id?: string;
  min_digits?: number;
  max_digits?: number;
  terminator?: string;
  timeout_s?: number;
  keys?: string;
  dest?: string;
  gateway?: string;
  cause?: string;
  sms_body?: string;
  match?: Record<string, string>;
}

export interface IvrEdge {
  id: string;
  source: string;
  target: string;
  when?: string;
}

export interface IvrGraph {
  schema_version: number;
  id: string;
  name: string;
  start: string;
  nodes: IvrNode[];
  edges: IvrEdge[];
  system?: boolean;
  description?: string;
}

/** IVR library row (list) or full document (get/save/activate). */
export interface IvrScript {
  slug: string;
  name: string;
  active_version: number;
  system?: boolean;
  description?: string;
  updated_at?: string;
  created_at?: string;
  version?: number;
  versions?: number[];
  graph?: IvrGraph;
}

export type CampaignStatus = "idle" | "running" | "paused" | "done";

export interface Campaign {
  id: string;
  name: string;
  script_slug: string;
  script_version?: number | null;
  delay_s: number;
  max_attempts: number;
  status: CampaignStatus | string;
  created_at?: string;
  updated_at?: string;
  contacts?: Record<string, unknown>[];
  contact_count?: number;
}

export interface CampaignReport {
  campaign: Campaign;
  attempts: unknown[];
  outcomes: Record<string, number>;
  attempt_count?: number;
  contact_count?: number;
  avg_duration_ms?: number | null;
  script_slug?: string;
  script_version?: number | null;
  delay_s?: number;
  max_attempts?: number;
}

export interface AdapterCard {
  id: string;
  label: string;
  enabled: boolean;
  has_key: boolean;
  needs_key: boolean;
  fallback?: string;
  provider?: string;
}

/** GET /api/adapters — flags only, never secret values. */
export interface AdaptersStatus {
  fish: AdapterCard | boolean;
  deepl: AdapterCard | boolean;
  sms: AdapterCard | boolean;
  notes?: string;
}

export interface CallProfile {
  name: string;
  dest?: string;
  customer_name?: string;
  cli?: string;
  sip_gateway_id?: string;
  caller_name?: string;
  business_name?: string;
  purpose?: string;
  otp_length?: number;
  expected_code?: string | null;
  pack?: string;
  scripts?: Record<string, string>;
  spoken_language?: string;
  identity_replay?: boolean;
  avmd_enabled?: boolean;
  recordings_enabled?: boolean;
  script?: string;
  ivr_id?: string;
  country?: string;
  lang?: string;
  allowed_countries?: string[];
  script_vars?: Record<string, string>;
}

export interface HealthResponse {
  ok?: boolean;
  setup: boolean;
  esl?: boolean;
  tts?: boolean;
  db?: boolean;
  mode?: RunMode;
  version?: string;
}

export interface DayCount {
  date: string;
  calls: number;
  /** Present on GET /api/stats series. Optional so older payloads still type-check. */
  human?: number;
  machine?: number;
  screening?: number;
  asr?: number;
}

/** One SIP trunk bucket. Ids stay sofia ids (cd, bitcall, …). Never a destination or CLI. */
export interface TrunkStat {
  trunk_id: string;
  calls: number;
  human: number;
  machine: number;
  screening: number;
  unknown: number;
  matched: number;
  /** 0–1 fraction, same scale as today.connect_rate. */
  connect_rate: number;
  avg_duration_s: number;
  series: DayCount[];
}

/** Nested today/all object from GET /api/stats. All fields optional. */
export interface StatsBucket {
  calls?: number;
  human?: number;
  machine?: number;
  screening?: number;
  unknown?: number;
  matched?: number;
  connect_rate?: number;
  avg_duration_s?: number;
}

/** One summary window. `human_connects` is answered-human, not stored AMD. */
export interface StatsKpiWindow {
  calls?: number;
  human_connects?: number;
  human?: number;
  machine?: number;
  screening_only?: number;
  screening?: number;
  screening_detected?: number;
  asr?: number;
  answered?: number;
  asr_of_answered?: number;
  avg_handle_s?: number;
  avg_duration_s?: number;
  avg_handle_ms?: number;
  otp_verified?: number;
}

/**
 * GET /api/stats `summary`. Nested today/d7/d30/all, plus flat fields if an
 * older payload still sends them.
 */
export interface StatsSummary {
  asr_definition?: string;
  today?: StatsKpiWindow;
  d7?: StatsKpiWindow;
  d30?: StatsKpiWindow;
  all?: StatsKpiWindow;
  total_calls?: number;
  calls?: number;
  human?: number;
  human_connects?: number;
  machine?: number;
  vm?: number;
  screening?: number;
  screening_only?: number;
  unknown?: number;
  asr?: number;
  connect_rate?: number;
  avg_handle_ms?: number;
  avg_handle_s?: number;
  avg_duration_ms?: number;
  avg_duration_s?: number;
  otp_verified?: number;
  otp_match?: number;
  matched?: number;
}

export interface StatsOutcomeRow {
  outcome?: string;
  count?: number;
}

export interface StatsOtpFunnel {
  prompted?: number;
  sent?: number;
  otp_sent?: number;
  collected?: number;
  collecting?: number;
  match?: number;
  mismatch?: number;
  timeout?: number;
}

/** Stored amd_result counts. Uppercase is the live key; lowercase is a fallback. */
export interface StatsAmdWindow {
  HUMAN?: number;
  MACHINE?: number;
  SCREENING?: number;
  UNKNOWN?: number;
  human?: number;
  machine?: number;
  screening?: number;
  unknown?: number;
}

/**
 * Raw GET /api/stats body. New desk shape is `summary` / `by_trunk` /
 * `by_country` / `outcomes` / `otp_funnel` / `amd_mix`. The live image may
 * still be `{today, all, series, trunks}`. Every field is optional.
 */
export interface StatsApiResponse {
  calls_today?: number;
  connect_rate?: number;
  machine?: number;
  otp_match?: number;
  sip?: unknown;
  avg_duration_ms?: number | null;
  series?: unknown;
  amd?: Partial<AmdSplit>;
  delta?: StatsDelta;
  today?: StatsBucket | null;
  all?: StatsBucket | null;
  trunks?: unknown;
  recent?: unknown;
  live?: unknown;
  summary?: StatsSummary | null;
  amd_mix?: Partial<Record<"today" | "d7" | "d30", StatsAmdWindow>> | null;
  by_trunk?: unknown;
  by_country?: unknown;
  outcomes?: unknown;
  otp_funnel?: StatsOtpFunnel | null;
}

export interface AmdSplit {
  HUMAN: number;
  MACHINE: number;
  SCREENING: number;
  UNKNOWN?: number;
}

export interface StatsDelta {
  calls_today?: number | null;
  connect_rate?: number | null;
  machine?: number | null;
  otp_match?: number | null;
  avg_duration_ms?: number | null;
}

/**
 * GET /api/stats. All fields optional so a partial sibling payload still renders.
 */
export interface StatsPayload {
  calls_today?: number;
  connect_rate?: number;
  machine?: number;
  otp_match?: number;
  sip?: string;
  avg_duration_ms?: number | null;
  series?: DayCount[];
  amd?: Partial<AmdSplit>;
  delta?: StatsDelta;
  trunks?: TrunkStat[];
  /** api-today when KPI/AMD came from nested `today`; history is the derived fallback. */
  source?: "api-today" | "history";
}

export interface AdminSecrets {
  has_fish?: boolean;
  has_esl_password?: boolean;
  has_bitcall?: boolean;
  has_deepl?: boolean;
}

/**
 * GET /api/admin/overview. Never includes secret values.
 */
export interface AdminOverview {
  version?: string;
  mode?: RunMode | string;
  esl?: boolean;
  tts?: boolean;
  db?: boolean;
  sip?: string | { text?: string; css?: string };
  setup?: boolean;
  leads_loaded?: number;
  paths?: Record<string, boolean>;
  secrets?: AdminSecrets;
  has_fish?: boolean;
  has_esl_password?: boolean;
  has_bitcall?: boolean;
  has_deepl?: boolean;
}

export interface SetupStatus {
  ready: boolean;
  esl_host?: string;
  esl_port?: number;
  has_esl_password?: boolean;
  has_fish_key?: boolean;
  has_bitcall?: boolean;
  has_deepl?: boolean;
  has_admin?: boolean;
  operator_name?: string;
  business_name?: string;
  voice_id?: string;
  bitcall_proxy?: string;
}

export interface SetupPayload {
  esl_host: string;
  esl_port: number;
  esl_password: string;
  bitcall_username: string;
  bitcall_password: string;
  bitcall_proxy: string;
  fish_api_key: string;
  voice_id: string;
  deepl_api_key: string;
  operator_name: string;
  business_name: string;
  admin_password: string;
  admin_password_confirm: string;
}

export interface SettingsPayload {
  operator_name?: string;
  business_name?: string;
  default_purpose?: string;
  rotation_pool?: string[];
  rotation_strategy?: "round_robin" | "least_used" | "random";
  decision_timeout_sec?: number;
  identity_replay?: boolean;
  avmd_enabled?: boolean;
  recordings_enabled?: boolean;
  has_esl_password?: boolean;
  has_fish_key?: boolean;
  has_bitcall?: boolean;
  has_cd?: boolean;
  has_deepl?: boolean;
  has_admin?: boolean;
}

/**
 * PATCH body for `POST /api/settings/secrets`. Every field is optional —
 * the backend only rotates the keys that are present. Empty strings are
 * treated as "leave unchanged" by the backend.
 */
export interface SecretPatch {
  esl_password?: string;
  fish_api_key?: string;
  bitcall_username?: string;
  bitcall_password?: string;
  bitcall_proxy?: string;
  cd_username?: string;
  cd_password?: string;
  cd_proxy?: string;
  deepl_api_key?: string;
  admin_password?: string;
}

/**
 * Response body for `POST /api/settings/secrets`. Booleans only —
 * never includes secret values.
 */
export interface SecretFlags {
  has_esl_password: boolean;
  has_fish_key: boolean;
  has_bitcall: boolean;
  has_cd: boolean;
  has_deepl: boolean;
  has_admin: boolean;
}

export type ToastLevel = "error" | "info" | "warn" | "ok";

export interface Toast {
  id: string;
  level: ToastLevel;
  message: string;
}

export type WsEvent =
  | { type: "session.state"; state: CallState; detail?: string | null; uuid?: string | null }
  | { type: "session.dtmf"; digits?: string; digit?: string; uuid?: string; kind?: string }
  | { type: "session.decision"; action: string }
  | { type: "session.ended"; line: string; hangup_cause?: string | null; amd?: string | null }
  | { type: "log.operator"; line: string; ts?: string }
  | { type: "log.debug"; line: string; ts?: string }
  | {
      type: "ui.hero";
      copy?: string;
      name?: string | null;
      dest?: string | null;
      cli?: string | null;
      amd?: string | null;
      amd_label?: string | null;
      attempt?: number;
      max_attempts?: number;
      park_remaining_s?: number | null;
      last_decision?: OperatorAction | null;
      detail?: string | null;
      far_end_class?: string | null;
      judgment_outcome?: string | null;
      trunk_id?: string | null;
      otp_state?: string | null;
    }
  | { type: "ui.code"; entered: string; expected: string | null; match?: boolean | null }
  | {
      type: "ui.actions";
      accept?: boolean;
      retry?: boolean;
      resend?: boolean;
      hangup?: boolean;
      otp_sent?: boolean;
      new_call?: boolean;
      redial?: boolean;
      next_lead?: boolean;
      listen?: boolean;
    }
  | { type: "sip.light"; text?: string; css?: string; status?: SipLight }
  | { type: "ui.park_tick"; remaining_s: number }
  | { type: "sys.health"; esl?: boolean; tts?: boolean; db?: boolean; mode?: RunMode }
  | {
      type: "hello";
      setup?: boolean;
      authenticated?: boolean;
      text?: string;
      css?: string;
      status?: SipLight;
      sip?: { text?: string; css?: string };
      amd_label?: string | null;
      amd?: string | null;
      ivr?: { id?: string | null; node?: string | null } | null;
      spoken_language?: string | null;
      pack?: string | null;
    }
  | { type: "snapshot"; console: ConsoleSnapshot };

export const EMPTY_ACTIONS: UiActions = {
  accept: false,
  retry: false,
  resend: false,
  hangup: false,
  otp_sent: false,
  new_call: true,
  redial: false,
  next_lead: false,
};

export const EMPTY_PARTY: CallParty = {
  dest: null,
  name: null,
  cli: null,
  amd: null,
  attempt: 0,
  max_attempts: 3,
  park_remaining_s: null,
  last_decision: null,
  hangup_cause: null,
  uuid: null,
};

export const EMPTY_SNAPSHOT: ConsoleSnapshot = {
  state: "IDLE",
  detail: null,
  heroCopy: null,
  party: EMPTY_PARTY,
  digits: "",
  expected_code: null,
  codeMatch: null,
  actions: EMPTY_ACTIONS,
  sip: "offline",
  sipText: "SIP offline",
  sipCss: "sip-down",
  mode: "offline",
  amd_label: null,
  far_end_class: null,
  judgment_outcome: null,
  trunk_id: null,
  otp_state: null,
  scriptId: null,
  scriptNode: null,
  spokenLanguage: null,
  digitKind: "",
};

export const ABNORMAL_CAUSES = new Set([
  "MACHINE",
  "OPERATOR_HANGUP",
  "OPERATOR_TIMEOUT",
  "NORMAL_TEMPORARY_FAILURE",
  "ERROR",
  "SCREENING_NO_CONNECT",
  "USER_BUSY",
  "NO_ANSWER",
  "NO_USER_RESPONSE",
  "ALLOTTED_TIMEOUT",
  "INTERWORKING",
  "GATEWAY_DOWN",
  "INVALID_GATEWAY",
]);

export function sipFromLight(text?: string, css?: string): { status: SipLight; text: string; css: string } {
  const rawText = (text ?? "").trim();
  const rawCss = (css ?? "").trim();
  let status: SipLight = "offline";
  if (rawText.includes("REGED") && !rawText.includes("NOREG")) status = "REGED";
  else if (rawText.includes("NOREG")) status = "NOREG";
  else if (rawText.includes("FAIL_WAIT")) status = "FAIL_WAIT";
  else if (rawText.includes("UNKNOWN")) status = "UNKNOWN";
  else if (rawCss === "sip-ok") status = "REGED";
  else if (rawCss === "sip-wait") status = "FAIL_WAIT";
  else status = "offline";
  const label =
    rawText ||
    (status === "offline" ? "SIP offline" : status === "UNKNOWN" ? "SIP UNKNOWN" : `SIP ${status}`);
  const klass =
    rawCss ||
    (status === "FAIL_WAIT"
      ? "sip-wait"
      : status === "REGED" || status === "NOREG"
        ? "sip-ok"
        : "sip-down");
  return { status, text: label, css: klass };
}
